import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { amenities } from '../api/endpoints.js';
import { amenityFormInput, type AmenityDetail, type AmenityFormInput } from '../api/schemas.js';
import { AmenityFields } from '../forms/amenity-fields.js';

export const CREATE_DEFAULTS: AmenityFormInput = {
  code: '',
  name: '',
  type: 'PROPERTY',
  category: 'GENERAL',
  iconUrl: '',
  description: '',
};

function defaultsFrom(amenity: AmenityDetail): AmenityFormInput {
  return {
    code: amenity.code,
    name: amenity.name,
    type: amenity.type,
    category: amenity.category,
    iconUrl: amenity.iconUrl ?? '',
    description: amenity.description ?? '',
  };
}

export function AmenityFormPage(): React.JSX.Element {
  const amenityId = useParams().amenityId;
  const isEdit = amenityId !== undefined;
  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.AMENITY_UPDATE : PERMISSIONS.AMENITY_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditAmenity id={amenityId} /> : <CreateAmenity />}
    </PermissionGuard>
  );
}

function CreateAmenity(): React.JSX.Element {
  const navigate = useNavigate();
  const create = amenities.useCreate();
  const form = useAppForm<AmenityFormInput>({
    schema: amenityFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Amenity created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync({ ...values, iconUrl: values.iconUrl || null });
      void navigate(`/amenities/${result.id}`);
    },
  });
  return (
    <FormPage
      title="New amenity"
      description="Add a tenant-wide amenity that properties and room types can share."
      backTo="/amenities"
      backLabel="Amenities"
      submitLabel="Create amenity"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <AmenityFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditAmenity({ id }: { readonly id: string }): React.JSX.Element {
  const query = amenities.useItem(id);
  if (query.isPending) return <LoadingState label="Loading amenity" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <EditAmenityForm id={id} amenity={query.data} />;
}

function EditAmenityForm({
  id,
  amenity,
}: {
  readonly id: string;
  readonly amenity: AmenityDetail;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = amenities.useUpdate();
  const form = useAppForm<AmenityFormInput>({
    schema: amenityFormInput,
    defaultValues: defaultsFrom(amenity),
    successMessage: 'Amenity updated',
    onSubmit: async (values) => {
      await update.mutateAsync({
        id,
        payload: { ...values, iconUrl: values.iconUrl || null, version: amenity.version },
      });
      void navigate(`/amenities/${id}`);
    },
  });
  return (
    <FormPage
      title={`Edit ${amenity.name}`}
      backTo={`/amenities/${id}`}
      backLabel="Amenity"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <AmenityFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
